import {
  EditOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { dbCols, UserRole } from '@cc/schema';
import { Show } from '@refinedev/antd';
import {
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useGetLocale,
} from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useActor } from '@xstate/react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  List,
  Result,
  Row,
  Segmented,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { format } from 'date-fns';
import qs from 'qs';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { TherapistActivityReactContext } from '../../App';
import { useClientOptions } from '../../hooks/client-options';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { IUser } from '../../providers';
import type {
  BehaviorExperiment,
  BehaviorExperimentObservation,
} from '../../schema/be-graphql-generated';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { CompleteForm, type CompletionFields } from './CompleteForm';
import { ObservationForm } from './ObservationForm';
import {
  BEHAVIOR_EXPERIMENT_DOMAIN,
  canEditExperiment,
  COEXISTENCE_COPY_KEYS,
  deriveStatus,
  durationDays,
  emotionName,
  experimentTitle,
  feelingName,
  isLegacyOutcomeObservation,
  observationCount,
  observationsOnRead,
  observationToFormValues,
  sanitizeEmotions,
  sanitizeInts,
  sortObservations,
  STATUS_TAG_COLOR,
  type ObservationFields,
  type ObservationOrder,
} from './esHelpers';
import { BEHAVIOR_EXPERIMENT_ES_FIELDS } from './fields';
import { useClientEra } from './useClientEra';

const { Text, Title } = Typography;

/**
 * E02 · Experiment Detail (portal, p3_detail) — board slice 87403e02 (BehaviorExperimentDetail) with
 * the RecordObservation (6ddd9efd) and CompleteExperiment (e4873b31) sections firing FROM this view
 * (founder G1 2026-08-29, portal-per-step resolved_v2 (d)+(e)): the design summary, ALL observations
 * (each opens for amending — D-v2-editable-observations), "+ Add observation" and "Finish".
 *
 * Reads the LEGACY row through `behaviorExperiment(_id)` with the additive fields (plan 04 D3, no ES
 * read models during coexistence); status, observation count and duration derive on read (handoff
 * §4). Every command's payload carries the row the projection wrote in the same transaction, so the
 * page updates from it — no refetch.
 *
 * Like the editor, it ROUTES BY THE CLIENT (plan 07 C3, `useClientEra`): when the server does not
 * route this client to ES for `behaviorExperiment` — or the log does not own the
 * row yet — this page offers no ES action at all and points at the standard form instead. Reading
 * the design and the runs never depends on the era.
 *
 * Route: `/tools/behaviorExperiments/show?behaviorExperimentId=behaviorExperiments/<key>` — the
 * model draws Detail and Design as two screens, so the view is its own route; the editor links here
 * and the list's View action opens it for rows the log owns.
 */
export const BehaviorExperimentView = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const getLocale = useGetLocale();
  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);
  const { briefClientInfo } = useClientOptions();

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const { behaviorExperimentId } =
    useMemo(
      () =>
        qs.parse(
          location.search.charAt(0) === '?'
            ? location.search.slice(1)
            : location.search,
        ),
      [location.search],
    ) || {};
  const rowId = behaviorExperimentId as string | undefined;

  useActivityTracking(rowId ? rowId : `${dbCols.behaviorExperiments}/show`);

  const {
    data: loaded,
    isLoading,
    isError,
  } = useCustom<BehaviorExperiment>({
    url: '',
    method: 'get',
    meta: {
      operation: 'behaviorExperiment',
      fields: BEHAVIOR_EXPERIMENT_ES_FIELDS,
      variables: { _id: { name: '_id', type: 'ID', value: rowId } },
    },
    queryOptions: {
      enabled: Boolean(rowId),
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });

  // the page state: the loaded row, then every command's read-your-writes payload
  const [row, setRow] = useState<BehaviorExperiment | null>(null);
  useEffect(() => {
    if (loaded?.data) {
      setRow(loaded.data);
      therapistActivitySend({
        type: 'LOADED',
        resource: loaded.data._id,
        clientId: loaded.data.userId,
      });
    }
  }, [loaded]);

  /**
   * The CLIENT's era, asked fresh on mount (plan 07 C3 / T112) — exactly as the editor asks it.
   * This view fires ES commands, so it must route by the same fact the SERVER routes by; a view
   * that assumed ES offered "Record observation" to a client the server routes to legacy and every
   * command came back ES_MODE_MISMATCH (founder QA 2026-09-02).
   */
  const { isEsClient, eraLoaded, client } = useClientEra(
    row?.userId,
    BEHAVIOR_EXPERIMENT_DOMAIN,
  );

  /**
   * May this therapist edit this client's design at all? THE SAME predicate the list's pencil asks
   * (`canEditExperiment`, esHelpers) — the shipped L02 rule of an edit relation plus a paid client
   * account. The view used to offer "Edit design" on every row while the list offered the pencil on
   * some, which is the inconsistency the founder reported (QA 2026-09-02, defect A). It reads the
   * client from `useClientEra`, i.e. `client(id)` — one client, asked by id, so the answer never
   * depends on which page of the practice roster happened to be fetched.
   */
  const { data: identity } = useGetIdentity<IUser>();
  const editGate = canEditExperiment(client, {
    isTherapistAdmin: identity?.db?.role === UserRole.THERAPIST_ADMIN,
  });

  const status = deriveStatus(row);
  const count = observationCount(row);
  const days = durationDays(row);
  const isLogOwned = Boolean(row?.experimentId);

  /**
   * Why this view can offer no ES action, if it cannot:
   *   `era`         — the server does not route this client to ES for the domain
   *                   (`esMode !== 'es'` or `behaviorExperiment` not ready for them). Every ES
   *                   command would be refused with ES_MODE_MISMATCH.
   *   `unconverted` — an es-routed client whose row the log does not own yet (no `experimentId`,
   *                   handoff §4 / T95a): the commands would fold to NOT_FOUND.
   * Either way the design and the observations stay readable and the therapist is pointed at the
   * standard form, which is where that experiment really is editable.
   */
  const eraBlock: 'era' | 'unconverted' | null = !eraLoaded
    ? null
    : !isEsClient
    ? 'era'
    : !isLogOwned
    ? 'unconverted'
    : null;
  const esActionsAvailable = eraLoaded && isEsClient && isLogOwned;

  /**
   * The runs the list shows. For a CONVERTED row with none of its own this is the single legacy
   * `outcome`, read as the FIRST observation — derived, marked, and NOT clickable, because no event
   * recorded it (founder ruling 2026-09-02; plan 04 §4.1 forbids inventing one). It counts.
   */
  const observations = useMemo(() => observationsOnRead(row), [row]);
  // a view preference, not a stored one (founder QA 2026-09-02): oldest first, as recorded
  const [observationOrder, setObservationOrder] =
    useState<ObservationOrder>('oldest');
  const orderedObservations = useMemo(
    () => sortObservations(observations, observationOrder),
    [observations, observationOrder],
  );

  const clientName = useMemo(() => {
    const c =
      client ?? briefClientInfo.find((x) => x._id === row?.userId) ?? null;
    const full = `${c?.firstName ?? ''} ${c?.lastName ?? ''}`.trim();
    return full || (row?.userId ?? '').replace('users/', '');
  }, [client, briefClientInfo, row?.userId]);

  const enumLabel = (key: string, idx?: number | null): string => {
    const arr = t(`shared.behaviorExperiment.${key}`, {
      returnObjects: true,
    }) as unknown as string[];
    return typeof idx === 'number' && Array.isArray(arr) ? arr[idx] ?? '' : '';
  };
  const hypothesisHeldShort = t(
    'pages.behaviorExperiment.hypothesisHeldShort',
    {
      returnObjects: true,
    },
  ) as unknown as string[];
  const fmtDate = (value: unknown): string => {
    if (!value) return '—';
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return '—';
    return format(
      d,
      currentLocale?.startsWith('ru')
        ? 'yyyy-MM-dd HH:mm'
        : 'yyyy-MM-dd hh:mm aaa',
    );
  };
  const emotionsText = (
    list:
      | ReadonlyArray<
          { id?: number | null; strength?: number | null } | null | undefined
        >
      | null
      | undefined,
  ): string =>
    sanitizeEmotions(list)
      .map((e) => `${emotionName(e.id, currentLocale)} (${e.strength})`)
      .join(', ');
  const feelingsText = (
    list: ReadonlyArray<number | null | undefined> | null | undefined,
  ): string =>
    sanitizeInts(list)
      .map((id) => feelingName(id, currentLocale))
      .join(', ');

  // ── the commands that fire FROM this view ─────────────────────────────────────────────────────
  const { mutateAsync: esMutate, isLoading: saving } = useCustomMutation<any>();

  const sendEsCommand = async (
    operation: string,
    inputType: string,
    value: Record<string, unknown>,
    extraFields: string[] = [],
  ) =>
    esMutate({
      url: '',
      method: 'post',
      values: {},
      meta: {
        operation,
        fields: [
          'experimentId',
          ...extraFields,
          { behaviorExperiment: BEHAVIOR_EXPERIMENT_ES_FIELDS },
        ],
        variables: { input: { name: 'input', type: inputType, value } },
      },
      // the outcome is reported below, by CODE — never the raw GraphQL error toast
      errorNotification: false,
    });

  /**
   * A refused command: the server's (localised) copy for a CODED reject, the generic copy
   * otherwise — except the two COEXISTENCE codes, which the server writes for the account owner
   * ("Your account was just updated") and which the therapist reading this page is not.
   */
  const reportFailure = (error: unknown) => {
    const { code, message: serverMessage } = graphqlErrorInfo(error);
    const therapistCopyKey = code ? COEXISTENCE_COPY_KEYS[code] : undefined;
    if (therapistCopyKey) {
      message.error(t(therapistCopyKey));
      return;
    }
    if (code && serverMessage) {
      message.error(serverMessage);
      return;
    }
    message.error(t('shared.behaviorExperiment.command.rejected'));
    Sentry.captureException(error);
  };

  const applyPayload = (payload: unknown) => {
    const next = (
      payload as { behaviorExperiment?: BehaviorExperiment | null } | null
    )?.behaviorExperiment;
    if (!next?._id) {
      // a silent null = no permission edge (the E07 gate, as the legacy path answers)
      throw new Error('behavior-experiment-command-missing-row');
    }
    setRow(next);
    therapistActivitySend({ type: 'UPDATED', resource: next._id });
  };

  const [observationDrawer, setObservationDrawer] = useState<{
    open: boolean;
    observation?: BehaviorExperimentObservation;
  }>({ open: false });
  const [completeOpen, setCompleteOpen] = useState(false);

  const saveObservation = async (fields: ObservationFields) => {
    if (!row?.experimentId) return;
    const editing = observationDrawer.observation;
    try {
      const res = await sendEsCommand(
        editing ? 'updateObservation' : 'recordObservation',
        editing ? 'UpdateObservationInput!' : 'RecordObservationInput!',
        {
          subject: row.userId,
          experimentId: row.experimentId,
          ...(editing ? { observationId: editing.observationId } : {}),
          ...fields,
        },
        ['observationId'],
      );
      applyPayload(res?.data);
      setObservationDrawer({ open: false });
      message.success(t('pages.behaviorExperiment.observationSaved'));
    } catch (error) {
      reportFailure(error);
    }
  };

  const saveCompletion = async (fields: CompletionFields) => {
    if (!row?.experimentId) return;
    try {
      const res = await sendEsCommand(
        'completeExperiment',
        'CompleteExperimentInput!',
        { subject: row.userId, experimentId: row.experimentId, ...fields },
      );
      applyPayload(res?.data);
      setCompleteOpen(false);
      message.success(t('pages.behaviorExperiment.completionSaved'));
    } catch (error) {
      reportFailure(error);
    }
  };

  // ── CTA gating (## Behavior: Add disabled when completed; both need an ACTIVATED design) ───────
  // The era/unconverted cases are NOT expressed as a disabled button any more: those commands do
  // not belong on this page at all, so the CTAs are absent and the notice above explains why.
  const addDisabledReason =
    status === 'draft'
      ? t('shared.behaviorExperiment.errors.NOT_ACTIVATED')
      : status === 'completed'
      ? t('shared.behaviorExperiment.errors.EXPERIMENT_COMPLETED')
      : null;
  const finishDisabledReason =
    status === 'draft'
      ? t('shared.behaviorExperiment.errors.NOT_ACTIVATED')
      : null;

  const title = row
    ? `${clientName} — ${
        experimentTitle(row) || t('pages.behaviorExperiment.viewTitle')
      }`
    : t('pages.behaviorExperiment.viewTitle');

  const editHref = `/tools/behaviorExperiments/create?behaviorExperimentId=${encodeURIComponent(
    row?._id ?? rowId ?? '',
  )}`;

  return (
    <Show
      resource="behaviorExperiments"
      isLoading={isLoading}
      breadcrumb={null}
      canDelete={false}
      canEdit={false}
      title={
        <Space wrap>
          <span data-testid="cc-behavior-experiment-view-title">{title}</span>
          {status ? (
            <Tag
              color={STATUS_TAG_COLOR[status]}
              data-testid="cc-behavior-experiment-view-status"
              data-status={status}
            >
              {t(`pages.behaviorExperiment.statuses.${status}`)}
            </Tag>
          ) : null}
        </Space>
      }
      headerButtons={
        <Space wrap>
          <Link to="/tools/behaviorExperiments">
            <Button icon={<UnorderedListOutlined />}>
              {t('pages.behaviorExperiment.backToList')}
            </Button>
          </Link>
          {/* "Edit design" is the ES surface's own way into the editor, so it belongs to the ES
              path: when the client is not routed ES for this domain (or the log does not own the
              row) the ONLY way in is the era notice's "Edit with the standard form", and offering
              two buttons to the same editor — one of them named for a form the therapist is not
              being sent to — is what the founder saw (QA 2026-09-02, defect B). Both are subject to
              the shared edit gate: no edit relation, no button anywhere. */}
          {row && esActionsAvailable && editGate.offered ? (
            <Tooltip title={t(editGate.tooltipKey)}>
              <Link to={editHref}>
                <Button
                  icon={<EditOutlined />}
                  disabled={!editGate.enabled}
                  data-testid="cc-behavior-experiment-view-edit-design"
                >
                  {t('pages.behaviorExperiment.editDesign')}
                </Button>
              </Link>
            </Tooltip>
          ) : null}
        </Space>
      }
    >
      {!isLoading && (isError || (rowId && !loaded?.data)) ? (
        <Result
          status="warning"
          title={t('shared.behaviorExperiment.loadError')}
          data-testid="cc-behavior-experiment-view-load-error"
        />
      ) : null}

      {row ? (
        <div
          data-testid="cc-behavior-experiment-view"
          data-era={isEsClient ? 'es' : 'legacy'}
          data-status={status ?? ''}
        >
          {eraBlock ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              data-testid="cc-behavior-experiment-view-era-notice"
              data-reason={eraBlock}
              message={t(
                eraBlock === 'era'
                  ? 'pages.behaviorExperiment.eraNotice'
                  : 'pages.behaviorExperiment.unconvertedRow',
              )}
              // the way out of the block — but only for a therapist who may take it. When the
              // edit gate says no, the notice keeps its sentence and loses its button.
              action={
                editGate.offered ? (
                  <Tooltip title={t(editGate.tooltipKey)}>
                    <Link to={editHref}>
                      <Button
                        size="small"
                        disabled={!editGate.enabled}
                        data-testid="cc-behavior-experiment-view-legacy-edit"
                      >
                        {t('pages.behaviorExperiment.editWithStandardForm')}
                      </Button>
                    </Link>
                  </Tooltip>
                ) : undefined
              }
            />
          ) : null}

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Card
                title={t('pages.behaviorExperiment.design')}
                data-testid="cc-behavior-experiment-view-design"
              >
                <Descriptions column={1} size="small" colon>
                  <Descriptions.Item
                    label={t('pages.behaviorExperiment.client')}
                  >
                    {clientName}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.purpose')}
                  >
                    {enumLabel('purposes', row.purpose)}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.design')}
                  >
                    {enumLabel('designs', row.design)}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.type')}
                  >
                    {[
                      enumLabel('typeVariants', row.type),
                      row.type === 0
                        ? enumLabel('activeSubtypes', row.activeSubtype)
                        : enumLabel(
                            'observationalSubtypes',
                            row.observationalSubtype,
                          ),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.hypothesisA')}
                  >
                    <span data-testid="cc-behavior-experiment-view-hypothesis-a">
                      {row.hypothesisA || '—'}
                    </span>
                    {typeof row.hypothesisAStrength === 'number' ? (
                      <Text type="secondary">
                        {' '}
                        · {row.hypothesisAStrength}/100
                      </Text>
                    ) : null}
                    {row.hypothesisAConfirmationCriteria ? (
                      <div>
                        <Text type="secondary">
                          {t(
                            'shared.behaviorExperiment.hypothesisAConfirmationCriteria',
                          )}
                          : {row.hypothesisAConfirmationCriteria}
                        </Text>
                      </div>
                    ) : null}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.hypothesisB')}
                  >
                    <span data-testid="cc-behavior-experiment-view-hypothesis-b">
                      {row.hypothesisB || '—'}
                    </span>
                    {typeof row.hypothesisBStrength === 'number' ? (
                      <Text type="secondary">
                        {' '}
                        · {row.hypothesisBStrength}/100
                      </Text>
                    ) : null}
                    {row.hypothesisBConfirmationCriteria ? (
                      <div>
                        <Text type="secondary">
                          {t(
                            'shared.behaviorExperiment.hypothesisBConfirmationCriteria',
                          )}
                          : {row.hypothesisBConfirmationCriteria}
                        </Text>
                      </div>
                    ) : null}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.experiment')}
                  >
                    {row.settings?.experiment || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.time')}
                  >
                    {row.settings?.time || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.place')}
                  >
                    {row.settings?.place || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.people')}
                  >
                    {row.settings?.people || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.resources')}
                  >
                    {row.settings?.resources || '—'}
                  </Descriptions.Item>
                  {row.settings?.survey ? (
                    <Descriptions.Item
                      label={t('shared.behaviorExperiment.survey')}
                    >
                      {row.settings.survey}
                    </Descriptions.Item>
                  ) : null}
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.safetyBehaviors')}
                  >
                    {row.safetyBehaviors || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t(
                      'shared.behaviorExperiment.helpfulCopingStrategies',
                    )}
                  >
                    {row.helpfulCopingStrategies || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('shared.behaviorExperiment.prediction')}
                  >
                    <div>{row.prediction?.behaviors || '—'}</div>
                    {row.prediction?.emotions?.length ? (
                      <div>
                        <Text type="secondary">
                          {t('shared.behaviorExperiment.expectedEmotions')}:{' '}
                          {emotionsText(row.prediction.emotions)}
                        </Text>
                      </div>
                    ) : null}
                    {row.prediction?.feelings?.length ? (
                      <div>
                        <Text type="secondary">
                          {t('shared.behaviorExperiment.expectedFeelings')}:{' '}
                          {feelingsText(row.prediction.feelings)}
                        </Text>
                      </div>
                    ) : null}
                    {row.prediction?.otherPredictions ? (
                      <div>
                        <Text type="secondary">
                          {t('shared.behaviorExperiment.otherPredictions')}:{' '}
                          {row.prediction.otherPredictions}
                        </Text>
                      </div>
                    ) : null}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('pages.behaviorExperiment.activatedAt')}
                  >
                    {fmtDate(
                      row.activatedAt ?? (isLogOwned ? row.createdAt : null),
                    )}
                  </Descriptions.Item>
                  {row.completedAt || status === 'completed' ? (
                    <Descriptions.Item
                      label={t('pages.behaviorExperiment.completedAt')}
                    >
                      {fmtDate(row.completedAt)}
                    </Descriptions.Item>
                  ) : null}
                </Descriptions>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card
                title={
                  <span data-testid="cc-behavior-experiment-view-observation-count">
                    {t('pages.behaviorExperiment.observationsCount', {
                      n: count,
                    })}
                  </span>
                }
                data-testid="cc-behavior-experiment-view-observations"
                extra={
                  <Space wrap>
                    {/* a pure client-side re-order of what is already on screen — nothing is
                        refetched, nothing is persisted (founder QA 2026-09-02) */}
                    {count > 0 ? (
                      <Segmented<ObservationOrder>
                        size="small"
                        value={observationOrder}
                        onChange={setObservationOrder}
                        aria-label={t(
                          'pages.behaviorExperiment.observationsOrder',
                        )}
                        data-testid="cc-behavior-experiment-observations-order"
                        options={[
                          {
                            value: 'oldest',
                            label: t(
                              'pages.behaviorExperiment.observationsOldestFirst',
                            ),
                          },
                          {
                            value: 'newest',
                            label: t(
                              'pages.behaviorExperiment.observationsNewestFirst',
                            ),
                          },
                        ]}
                      />
                    ) : null}
                    {days !== undefined ? (
                      <Text type="secondary">
                        {t('pages.behaviorExperiment.duration')}:{' '}
                        {t('pages.behaviorExperiment.days', { n: days })}
                      </Text>
                    ) : null}
                  </Space>
                }
              >
                {count === 0 ? (
                  <Text type="secondary">
                    {t('pages.behaviorExperiment.noObservations')}
                  </Text>
                ) : (
                  <List
                    size="small"
                    dataSource={orderedObservations}
                    renderItem={(o, index) => {
                      const isLegacy = isLegacyOutcomeObservation(o);
                      // amending is an ES command too — a client the server routes to legacy gets
                      // a readable run, never a drawer that cannot save (founder QA 2026-09-02)
                      const canAmend = !isLegacy && esActionsAvailable;
                      return (
                        <List.Item
                          key={o.observationId}
                          style={canAmend ? { cursor: 'pointer' } : undefined}
                          onClick={
                            canAmend
                              ? () =>
                                  setObservationDrawer({
                                    open: true,
                                    observation:
                                      o as BehaviorExperimentObservation,
                                  })
                              : undefined
                          }
                          data-testid={
                            isLegacy
                              ? 'cc-behavior-experiment-view-legacy-outcome'
                              : `cc-behavior-experiment-observation-${index}`
                          }
                          data-legacy={isLegacy ? 'true' : undefined}
                          actions={[
                            typeof o.rating === 'number' ? (
                              <Text key="rating" strong>
                                {o.rating}
                              </Text>
                            ) : null,
                          ]}
                        >
                          <List.Item.Meta
                            title={
                              <Space wrap>
                                <span>{fmtDate(o.occurredAt)}</span>
                                {typeof o.whichHypothesisHeld === 'number' ? (
                                  <Tag>
                                    {hypothesisHeldShort?.[
                                      o.whichHypothesisHeld
                                    ] ?? ''}
                                  </Tag>
                                ) : null}
                                {isLegacy ? (
                                  <Tooltip
                                    title={t(
                                      'pages.behaviorExperiment.legacyObservationHint',
                                    )}
                                  >
                                    <Tag color="default">
                                      {t(
                                        'pages.behaviorExperiment.legacyObservationNote',
                                      )}
                                    </Tag>
                                  </Tooltip>
                                ) : null}
                              </Space>
                            }
                            description={
                              [o.whatHappened, o.behaviors, o.learnings]
                                .filter(Boolean)
                                .join(' · ') ||
                              [
                                emotionsText(o.emotions),
                                feelingsText(o.feelings),
                              ]
                                .filter(Boolean)
                                .join(' · ')
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                )}

                {/* No ES action at all when the server does not route this client to the new
                    experience for the domain, or the log does not own the row: the notice above
                    says why, and the standard form is where that experiment is editable. */}
                {esActionsAvailable ? (
                  <Space wrap style={{ marginTop: 16 }}>
                    <Tooltip title={addDisabledReason ?? ''}>
                      <Button
                        icon={<PlusOutlined />}
                        disabled={Boolean(addDisabledReason)}
                        onClick={() => setObservationDrawer({ open: true })}
                        data-testid="cc-behavior-experiment-add-observation"
                      >
                        {t('pages.behaviorExperiment.addObservation')}
                      </Button>
                    </Tooltip>
                    {/* the FIRST completion CTA lives here, beside the runs it closes; once the
                        experiment is completed the affordance is "Update completion notes" and it
                        moves into that section's header (founder QA 2026-09-02) */}
                    {status !== 'completed' ? (
                      <Tooltip title={finishDisabledReason ?? ''}>
                        <Button
                          type="primary"
                          disabled={Boolean(finishDisabledReason)}
                          onClick={() => setCompleteOpen(true)}
                          data-testid="cc-behavior-experiment-finish"
                        >
                          {t('pages.behaviorExperiment.finishExperiment')}
                        </Button>
                      </Tooltip>
                    ) : null}
                  </Space>
                ) : null}
              </Card>

              {status === 'completed' ? (
                <Card
                  title={t('pages.behaviorExperiment.completion')}
                  style={{ marginTop: 24 }}
                  data-testid="cc-behavior-experiment-view-completion"
                  extra={
                    esActionsAvailable ? (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => setCompleteOpen(true)}
                        data-testid="cc-behavior-experiment-finish"
                      >
                        {t('pages.behaviorExperiment.updateCompletion')}
                      </Button>
                    ) : null
                  }
                >
                  <Descriptions column={1} size="small">
                    <Descriptions.Item
                      label={t('shared.behaviorExperiment.patientReflections')}
                    >
                      {row.outcome?.patientReflections || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t(
                        'shared.behaviorExperiment.therapistReflections',
                      )}
                    >
                      <span data-testid="cc-behavior-experiment-view-therapist-reflections">
                        {row.outcome?.therapistReflections || '—'}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t('shared.behaviorExperiment.newThought')}
                    >
                      {row.outcome?.newThought || '—'}
                      {typeof row.outcome?.newThoughtStrength === 'number' ? (
                        <Text type="secondary">
                          {' '}
                          · {row.outcome.newThoughtStrength}/100
                        </Text>
                      ) : null}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t('shared.behaviorExperiment.oldThoughtStrength')}
                    >
                      {typeof row.outcome?.oldThoughtStrength === 'number'
                        ? `${row.outcome.oldThoughtStrength}/100`
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t('shared.behaviorExperiment.newLearnings')}
                    >
                      {row.outcome?.newLearnings || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t(
                        'shared.behaviorExperiment.meaningForOriginalBelief',
                      )}
                    >
                      {row.outcome?.meaningForOriginalBelief || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t(
                        'shared.behaviorExperiment.followUpExperimentsIdeas',
                      )}
                    >
                      {row.followUpExperimentsIdeas || '—'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              ) : null}
            </Col>
          </Row>

          <ObservationForm
            open={observationDrawer.open}
            title={t(
              observationDrawer.observation
                ? 'pages.behaviorExperiment.editObservation'
                : 'pages.behaviorExperiment.recordObservation',
            )}
            subtitle={t('pages.behaviorExperiment.inSession', {
              client: clientName,
            })}
            initial={
              observationDrawer.observation
                ? observationToFormValues(observationDrawer.observation)
                : undefined
            }
            saving={saving}
            onClose={() => setObservationDrawer({ open: false })}
            onSubmit={saveObservation}
          />

          <CompleteForm
            open={completeOpen}
            subtitle={t('pages.behaviorExperiment.completeSubtitle', {
              client: clientName,
            })}
            observationCount={count}
            durationDays={days}
            isRevision={status === 'completed'}
            // A converted row is never `completed` (founder ruling 2026-09-02) — but the leaves its
            // legacy `outcome` carried are exactly what the therapist wrote up, so the completion
            // drawer PRE-FILLS from them. Saving turns them into a real ExperimentCompleted.
            initial={{
              patientReflections: row.outcome?.patientReflections,
              therapistReflections: row.outcome?.therapistReflections,
              newThought: row.outcome?.newThought,
              newThoughtStrength: row.outcome?.newThoughtStrength,
              oldThoughtStrength: row.outcome?.oldThoughtStrength,
              newLearnings: row.outcome?.newLearnings,
              meaningForOriginalBelief: row.outcome?.meaningForOriginalBelief,
              followUpExperimentsIdeas: row.followUpExperimentsIdeas,
            }}
            saving={saving}
            onClose={() => setCompleteOpen(false)}
            onSubmit={saveCompletion}
          />
        </div>
      ) : null}
      {!row && !isLoading && !rowId ? (
        <Title level={5}>{t('shared.behaviorExperiment.loadError')}</Title>
      ) : null}
    </Show>
  );
};
